// ================================================================================================
// Web Thing
// ================================================================================================
import _ from "lodash";
import { isDefined, getUrlParameters, getRandomSmallKey } from "@Utils/Micro";
import { IWidgetDataValidateResp } from "@UI/Widgets/WidgetStructs";
import { Localization } from '@Primitive/Localization/Localization';
import { Plegma } from './Api';
import { DebugEx } from '@Utils/DebugEx';
import { StateStatus, WebThingKeys, WebThingPortType } from './WebNodeTypes';
import { WebNode } from './WebNode';
import type { WebModule } from "./WebModule";
// ================================================================================================
export class WebThingPort {
    // --------------------------------------------------------------------------------------------
    public PlegmaPort: Plegma.Port;
    public PortType: WebThingPortType;
    public WebThingKey: string;
    public LastStateStatus: StateStatus;
    // --------------------------------------------------------------------------------------------
    // Events
    public onPortEvent: (event: Plegma.PortEvent, port: WebThingPort) => void;
    // --------------------------------------------------------------------------------------------
    // CB
    public cbPortDataValidation: (data: any) => IWidgetDataValidateResp;
    // --------------------------------------------------------------------------------------------
    // Properties
    get PortKey(): string { return this.PlegmaPort.PortKey; }
    get State(): any { return this.PlegmaPort.State; }
    set State(state: any) { this.PlegmaPort.State = state; }
    // --------------------------------------------------------------------------------------------
    constructor(
        port: Plegma.Port,
        portType: WebThingPortType,
        cbPortDataValidation?: (data: any) => IWidgetDataValidateResp,
        onPortEvent?: (event: Plegma.PortEvent, port: WebThingPort) => void) {

        if (!isDefined(port.PortKey)) {
            DebugEx.TraceCritical("Don't use null/undefined port key!!!!", port);
        }
        else if (port.PortKey.indexOf("-") != -1) {
            DebugEx.TraceCritical("Don't use - in port key!!!!", port);
        }

        this.PlegmaPort = port;
        this.PortType = portType
        this.onPortEvent = onPortEvent;
        this.cbPortDataValidation = cbPortDataValidation;
        this.LastStateStatus = StateStatus.NoData;
    }
    // --------------------------------------------------------------------------------------------
}
// ================================================================================================
export class WebThing {
    // --------------------------------------------------------------------------------------------
    public Name: string;
    // --------------------------------------------------------------------------------------------
    public PlegmaThing: Plegma.Thing;
    public Ports: { [key: string]: WebThingPort };
    // --------------------------------------------------------------------------------------------
    private PortsIncCount: number;
    // --------------------------------------------------------------------------------------------
    // Properties
    get ThingKey(): string { return this.PlegmaThing.ThingKey; }
    // --------------------------------------------------------------------------------------------
    private ModuleKey: string;
    private NodeKey: string;
    get webNode(): WebNode { return WebNode.GetInstanceKey(this.NodeKey); }
    get webModule(): WebModule { return this.webNode?.GetModule(this.ModuleKey) }
    // --------------------------------------------------------------------------------------------
    constructor(module: WebModule, thing: Plegma.Thing, ports?: WebThingPort[]) {
        this.NodeKey = module.NodeKey;
        this.ModuleKey = module.ModuleKey;
        this.PortsIncCount = 100; // Big starting number for correct starting of ports

        // Update thing key with module name
        this.Name = thing.Name;
        thing.Name = this.Name + "(" + module.Name + ")";
        thing.ThingKey = module.ModuleKey + "_" + thing.ThingKey;

        this.PlegmaThing = thing;
        this.PlegmaThing.Ports = [];
        this.PlegmaThing.Hierarchy = "";

        // Pass all ports
        this.Ports = {};
        _.forEach(ports, p => {
            this.Ports[p.PortKey] = p;

            // link the port to this thing
            p.WebThingKey = thing.ThingKey;

            // Link the plegma ports to plegma thing
            if (!this.webNode?.DummyNode) {
                // Normalized data of plegma
                if (_.isNil(p.PlegmaPort.ConfFlags))
                    p.PlegmaPort.ConfFlags = Plegma.ePortConf.None;

                this.PlegmaThing.Ports.push(p.PlegmaPort);
            }
        });

        // Be sure that we have the user info port
        this.CreateUserInfoPort();

        // Connect the thing to module
        this.webModule.AddThing(this);
    }
    // --------------------------------------------------------------------------------------------
    public CreateUserInfoPort() {
        if (!this.webNode?.DummyNode) {
            if (!_.has(this.Ports, WebThingKeys.UserEmailKey)) {
                // Add port for user things
                let newPort = new WebThingPort({
                    Name: "User Email",
                    PortKey: WebThingKeys.UserEmailKey,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                    State: this.webNode?.YodiWS.UserInfo.Email
                }, WebThingPortType.Data);

                newPort.WebThingKey = this.ThingKey;
                this.Ports[newPort.PortKey] = newPort;
                this.PlegmaThing.Ports.push(newPort.PlegmaPort);
            }

            if (!_.has(this.Ports, WebThingKeys.URLParamsKey)) {
                // Add port for user things
                let newPort = new WebThingPort({
                    Name: "URL Params",
                    PortKey: WebThingKeys.URLParamsKey,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                    State: JSON.stringify(getUrlParameters())
                }, WebThingPortType.Data);

                newPort.WebThingKey = this.ThingKey;
                this.Ports[newPort.PortKey] = newPort;
                this.PlegmaThing.Ports.push(newPort.PlegmaPort);
            }

            if (!_.has(this.Ports, WebThingKeys.BoardLanguageKey)) {
                let localization = Localization.GetInstance(this.NodeKey);

                // Add port for user things
                let newPort = new WebThingPort({
                    Name: "Language Code",
                    PortKey: WebThingKeys.BoardLanguageKey,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                    State: (localization != null) ? localization.SelectedLanguage : ""
                }, WebThingPortType.Data);

                newPort.WebThingKey = this.ThingKey;
                this.Ports[newPort.PortKey] = newPort;
                this.PlegmaThing.Ports.push(newPort.PlegmaPort);
            } else {
                let localization = Localization.GetInstance(this.NodeKey);
                this.Ports[WebThingKeys.BoardLanguageKey].State = (localization != null) ? localization.SelectedLanguage : "";
            }

            if (!_.has(this.Ports, WebThingKeys.Location)) {
                // Add port for user things
                let newPort = new WebThingPort({
                    Name: "Location",
                    PortKey: WebThingKeys.Location,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                    State: "Location" // TODO: read the initial state
                }, WebThingPortType.Data);

                newPort.WebThingKey = this.ThingKey;
                this.Ports[newPort.PortKey] = newPort;
                this.PlegmaThing.Ports.push(newPort.PlegmaPort);
            }

            if (!_.has(this.Ports, WebThingKeys.Cookie)) {
                // Get cookie from local storage
                let cookie = localStorage.getItem(this.NodeKey + "__COOKIE_KEY__");

                // Add port for user things
                let newPort = new WebThingPort({
                    Name: "Cookie",
                    PortKey: WebThingKeys.Cookie,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                    State: isDefined(cookie) ? cookie : null
                }, WebThingPortType.Data);

                newPort.WebThingKey = this.ThingKey;
                this.Ports[newPort.PortKey] = newPort;
                this.PlegmaThing.Ports.push(newPort.PlegmaPort);
            }

            if (!_.has(this.Ports, WebThingKeys.ToolbarIconClick)) {
                // Add port for user things
                let newPort = new WebThingPort({
                    Name: "Widget Toolbar Icon Clicked",
                    PortKey: WebThingKeys.ToolbarIconClick,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: ""
                }, WebThingPortType.Trigger);

                newPort.WebThingKey = this.ThingKey;
                this.Ports[newPort.PortKey] = newPort;
                this.PlegmaThing.Ports.push(newPort.PlegmaPort);
            }
        }

        if (!_.has(this.Ports, WebThingKeys.WidgetSettings)) {
            // Add port for user things
            let newPort = new WebThingPort({
                Name: "Widget Settings",
                PortKey: WebThingKeys.WidgetSettings,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.Hidden | Plegma.ePortConf.IsMetadata,
                State: ""
            }, WebThingPortType.Data);

            newPort.WebThingKey = this.ThingKey;
            this.Ports[newPort.PortKey] = newPort;

            if (!this.webNode?.DummyNode)
                this.PlegmaThing.Ports.push(newPort.PlegmaPort);
        }
    }
    // --------------------------------------------------------------------------------------------
    public SendPortEvent(portKey: string, state: any, origState: any) {
        if (_.has(this.Ports, portKey)) {
            let port = this.Ports[portKey];

            // Save state
            port.State = state;

            // Send ports
            this.webModule.SendPortEvent(this, port, state, origState);
        }
    }
    // --------------------------------------------------------------------------------------------
    public AddPort(port: WebThingPort, updateServer: boolean = false) {
        // link the port to this thing
        port.WebThingKey = this.ThingKey;

        this.Ports[port.PortKey] = port;
        if (!this.webNode?.DummyNode)
            this.PlegmaThing.Ports = _.map(this.Ports, t => t.PlegmaPort);

        // Update the module with the new thing ports
        if (updateServer)
            this.webModule.UpdateThing(this);
    }
    // --------------------------------------------------------------------------------------------
    public GetPort(portKey: string): WebThingPort | null {
        if (_.has(this.Ports, portKey)) {
            return this.Ports[portKey];
        }
        return null;
    }
    // --------------------------------------------------------------------------------------------
    public GetNextGeneratedPortKey(): string {
        this.PortsIncCount++;
        return this.PortsIncCount + "_" + getRandomSmallKey();
    }
    // --------------------------------------------------------------------------------------------
    public Rename(name: string) {
        this.Name = name;
        if (!this.webNode?.DummyNode)
            this.PlegmaThing.Name = this.Name + "(" + this.webModule.Name + ")";

        // Update the module with the new naming
        this.webModule.UpdateThing(this);
    }
    // --------------------------------------------------------------------------------------------
}
// ================================================================================================